import { Button, CardBackground, Typography } from "@hellocarbo/lasagne";
import { use, useEffect, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { Link, useLoaderData, useNavigate } from "react-router";

import Loader from "@/components/ui/Loader";
import { UserContext } from "@/contexts";
import { TTrajectory, TTrajectoryStep } from "@/types";

export async function loader() {
  const trajectoryResponse = await fetch("/api/client/trajectories");

  /*
   * In this page we can have an expected 404 for Start user : their FreeTrial is completed but don't have any Trajectory
   * We will display the feature first pages, but won't allow / redirect to the later pages
   */
  if (trajectoryResponse.status == 404) {
    return { notFound: true };
  }

  const { data: trajectoryData } = (await trajectoryResponse.json()) as {
    data: TTrajectory;
  };

  const trajectoryStepsResponse = await fetch("/api/client/trajectory_steps");

  if (trajectoryStepsResponse.status == 404) {
    return { notFound: true };
  }

  const { data: trajectoryStepsData } =
    (await trajectoryStepsResponse.json()) as { data: Array<TTrajectoryStep> };

  return { trajectoryData, trajectoryStepsData };
}

function TrajectoryIntroduction() {
  const { t } = useTranslation(["trajectory"]);
  const { user } = use(UserContext);
  const { notFound, trajectoryData, trajectoryStepsData } = useLoaderData();
  const navigate = useNavigate();
  const [isRedirecting, setIsRedirecting] = useState(false);

  /*
   * We want to skip intro and redirect users towards last page of the feature if they already have
   * A) all the necessary data (steps well defined)
   * AND B) the right access because of their plan and/or option (handled by /bolo/app/policies/trajectory_policy.rb)
   * We do this check here in the component rather than in the loader to access the UserContext data
   */
  useEffect(() => {
    if (
      !notFound &&
      user?.policies?.trajectory?.queries["index"] &&
      trajectoryStepsData.length > 0
    ) {
      setIsRedirecting(true);
      navigate("/trajectory/action-plan", { replace: true });
    }
  }, [user, trajectoryData, trajectoryStepsData, navigate]);

  if (isRedirecting) {
    return <Loader />;
  }

  return (
    <CardBackground>
      <Typography as="h1" step="2" weight="bold" className="mb-xs">
        {t("trajectory:introduction.title")}
      </Typography>
      <Typography className="mb-xl">
        <Trans
          t={t}
          i18nKey="trajectory:introduction.text"
          components={{
            1: <br />,
          }}
        />
      </Typography>
      <div className="flex justify-center">
        <Button as={Link} to="/trajectory/creation/context">
          {t("trajectory:introduction.startMyTrajectory")}
        </Button>
      </div>
    </CardBackground>
  );
}

export default TrajectoryIntroduction;
